import { config } from '../config/environment';

// Define the TikTok Pixel interface
interface TikTokPixel {
  methods: string[];
  setAndDefer: (t: any, e: string) => void;
  instance: (t: string) => any;
  load: (e: string, n?: any) => void;
  init: (pixelId: string) => void;
  track: (event: string, params?: any) => void;
  page: () => void;
  identify: (params?: any) => void;
  _i: Record<string, any[] & { _u?: string }>;
  _t: Record<string, number>;
  _o: Record<string, any>;
}

// Extend the Window interface
declare global {
  interface Window {
    TiktokAnalyticsObject: string;
    ttq: TikTokPixel;
  }
}

// Helper function to hash data with SHA-256 using Web Crypto API
const hashData = async (data: string): Promise<string> => {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(byte => byte.toString(16).padStart(2, '0')).join('');
};

// Interface for user identification data
interface TikTokIdentifyData {
  email?: string;
  phoneNumber?: string;
  externalId?: string;
}

// Interface for content item
interface TikTokContentItem {
  content_id: string;
  content_type: 'product' | 'product_group';
  content_name: string;
}

// Interface for tracking event data
interface TikTokTrackData {
  contents: TikTokContentItem[];
  value?: number;
  currency?: string;
  search_string?: string;
}

// Function to identify user
export const identifyTikTokUser = async (data: TikTokIdentifyData) => {
  if (!window.ttq) return;

  const identifyData: Record<string, string> = {};
  
  if (data.email) {
    identifyData.email = await hashData(data.email);
  }
  if (data.phoneNumber) {
    identifyData.phone_number = await hashData(data.phoneNumber);
  }
  if (data.externalId) {
    identifyData.external_id = await hashData(data.externalId);
  }

  window.ttq.identify(identifyData);
};

// Function to track events
export const trackTikTokEvent = (
  eventName: string,
  data: TikTokTrackData
) => {
  if (!window.ttq) return;
  
  window.ttq.track(eventName, data);
};

// Specific function for tracking collab application completion
export const trackCollabApplication = async (
  userId: string,
  collabData: {
    collabId: string;
    collabName: string;
    brandName: string;
    value?: number;
  }
) => {
  // First identify the user
  await identifyTikTokUser({
    externalId: userId
  });

  // Then track the completion event
  trackTikTokEvent('CompleteRegistration', {
    contents: [{
      content_id: collabData.collabId,
      content_type: 'product',
      content_name: `${collabData.brandName} - ${collabData.collabName}`
    }],
    value: collabData.value || 0,
    currency: 'USD'
  });
};

export const initTikTokPixel = () => {
  // Only initialize if we're in the browser and haven't already initialized
  if (typeof window === 'undefined' || window.ttq) return;

  // TikTok Pixel Code
  (function (w: Window, d: Document, t: string) {
    w.TiktokAnalyticsObject = t;
    const ttq = ((w as any)[t] = ((w as any)[t] || []) as TikTokPixel);
    
    ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie"];
    
    ttq.setAndDefer = function(t: any, e: string) {
      t[e] = function() {
        t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
      };
    };
    
    for (let i = 0; i < ttq.methods.length; i++) {
      ttq.setAndDefer(ttq, ttq.methods[i]);
    }
    
    ttq.instance = function(t: string) {
      const instance = ttq._i[t] || [];
      for (let n = 0; n < ttq.methods.length; n++) {
        ttq.setAndDefer(instance, ttq.methods[n]);
      }
      return instance;
    };
    
    ttq.load = function(e: string, n?: any) {
      const i = "https://analytics.tiktok.com/i18n/pixel/events.js";
      ttq._i = ttq._i || {};
      ttq._i[e] = [];
      (ttq._i[e] as any)._u = i;
      ttq._t = ttq._t || {};
      ttq._t[e] = +new Date();
      ttq._o = ttq._o || {};
      ttq._o[e] = n || {};
      
      const o = document.createElement("script");
      o.type = "text/javascript";
      o.async = true;
      o.src = i + "?sdkid=" + e + "&lib=" + t;
      
      const scripts = document.getElementsByTagName("script") as HTMLCollectionOf<HTMLScriptElement>;
      if (scripts.length > 0 && scripts[0].parentNode) {
        scripts[0].parentNode.insertBefore(o, scripts[0]);
      }
    };

    // Initialize with Pixel ID from environment config
    ttq.load(config.tiktokPixelId);
    ttq.page();
  })(window, document, 'ttq');
}; 